import { useState } from 'react';
import { Alert, Sheet } from '../../components/Overlay.tsx';
import { Icon } from '../../components/Icon.tsx';
import { ErrorState, Loading } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { addFriendByCode, reissueInviteCode, useInviteCode } from '../../api/rooms.ts';
import type { Invite } from '../../api/rooms.ts';

/**
 * D1-1 — 친구 추가 (초대 코드).
 *
 * 친구를 맺는 길이 둘이고, 둘 다 여기 있습니다 — **내 코드를 건네거나,
 * 받은 코드를 적어 넣거나.** 한 시트에 둔 것은 둘이 같은 일의 양쪽이기
 * 때문입니다. 화면을 나누면 "나는 어느 쪽이었지"를 매번 고르게 됩니다.
 *
 * 코드는 **사람이 눈으로 읽고 손으로 받아 적는 값**입니다. 그래서
 *   · 네 글자씩 끊어 크게 보여 주고 (ACDE-FGHJ)
 *   · 눌러서 복사할 수 있고
 *   · 적어 넣을 때 하이픈 · 공백 · 소문자를 신경 쓰지 않아도 됩니다
 *
 * **코드는 비밀입니다.** 아는 사람은 나를 친구로 추가할 수 있습니다.
 * 그래서 새로 뽑을 수 있게 두되, 옛 코드가 그 순간 죽는다는 것을 누르기
 * 전에 알립니다 — 이미 건넨 사람도 못 쓰게 됩니다.
 */

interface Props {
  onClose: () => void;
  /** 친구가 늘었으니 목록을 다시 부르라는 신호 */
  onAdded: () => void;
}

export function InviteSheet({ onClose, onAdded }: Props) {
  const { data: invite, loading, error, reload } = useInviteCode(true);

  return (
    <Sheet title="친구 추가" onClose={onClose}>
      <div className="sheet__scroll">
        {loading && <Loading label="초대 코드를 불러오는 중" />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {invite && <MyCode invite={invite} />}

        <div className="rule" />

        <EnterCode
          onAdded={() => {
            onAdded();
            onClose();
          }}
        />
      </div>
    </Sheet>
  );
}

/* ── 내 코드 건네기 ───────────────────────────────────────────── */

function MyCode({ invite }: { invite: Invite }) {
  const [shown, setShown] = useState(invite);
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * 클립보드는 막힐 수 있습니다 — 사파리는 사용자 동작 없이 부르면 거절하고,
   * http 로 열면 API 자체가 없습니다. 실패해도 코드는 화면에 그대로 보이므로
   * 손으로 읽어 적으면 됩니다. 그래서 실패를 오류로 띄우지 않습니다.
   */
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shown.pretty);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setFailure('복사하지 못했습니다. 코드를 직접 읽어 적어 주세요.');
    }
  };

  const reissue = async () => {
    setBusy(true);
    setFailure(null);
    try {
      setShown(await reissueInviteCode());
      setConfirming(false);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '새 코드를 만들지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="field">
      <span className="field__label">내 코드</span>

      {/* 눌러서 복사. 코드 자체가 버튼이라 어디를 눌러야 하는지 고민이 없습니다 */}
      <button type="button" className="invite__code" onClick={() => void copy()}>
        <span className="invite__value">{shown.pretty}</span>
        <span className="invite__copy">
          <Icon name={copied ? 'check' : 'share'} size={15} />
          {copied ? '복사됨' : '복사'}
        </span>
      </button>

      <p className="hint">
        이 코드를 친구에게 건네면 친구가 나를 추가합니다. <b>코드를 아는 사람은 나를 친구로
        추가할 수 있으니</b> 아무 데나 적어 두지 마세요.
      </p>

      {failure && <p className="failure">{failure}</p>}

      <button
        type="button"
        className="btn btn--secondary"
        onClick={() => setConfirming(true)}
      >
        새 코드 만들기
      </button>

      {confirming && (
        <Alert
          title="새 코드를 만들까요?"
          onClose={() => setConfirming(false)}
          actions={
            <>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirming(false)}
              >
                그대로 두기
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy}
                onClick={() => void reissue()}
              >
                {busy ? '만드는 중…' : '새로 만들기'}
              </button>
            </>
          }
        >
          {/* 되돌릴 수 없는 쪽을 먼저 적습니다 */}
          <p>
            지금 코드는 <b>바로 쓸 수 없게 됩니다.</b> 이미 건넨 사람도 이 코드로는 나를
            추가하지 못합니다.
          </p>
          <p>이미 친구가 된 사람은 그대로 남습니다.</p>
        </Alert>
      )}
    </div>
  );
}

/* ── 받은 코드 적어 넣기 ──────────────────────────────────────── */

function EnterCode({ onAdded }: { onAdded: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);

  // 여덟 글자를 채웠는지만 봅니다. 하이픈 · 공백 · 소문자는 서버가 걸러 냅니다
  const filled = code.replace(/[^0-9a-zA-Z]/g, '').length >= 8;

  const submit = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const friend = await addFriendByCode(code);
      setAdded(friend.name ?? '친구');
      setCode('');
      // 이름을 잠깐 보여 준 뒤 닫습니다 — 누가 추가됐는지 모르고 닫히면 불안합니다
      window.setTimeout(onAdded, 1200);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '추가하지 못했습니다.');
      setBusy(false);
    }
  };

  if (added) {
    return (
      <div className="field">
        <p className="invite__done">
          <Icon name="check" size={18} />
          <b>{added}</b> 님과 친구가 되었습니다
        </p>
      </div>
    );
  }

  return (
    <div className="field">
      <span className="field__label">받은 코드</span>
      <input
        className="input invite__input"
        value={code}
        placeholder="ACDE-FGHJ"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        aria-label="친구에게 받은 초대 코드"
        onChange={(e) => {
          setCode(e.target.value);
          setFailure(null);
        }}
      />
      {failure && <p className="failure">{failure}</p>}
      <p className="hint">대문자 · 하이픈은 신경 쓰지 않아도 됩니다.</p>

      <button
        type="button"
        className="btn btn--primary"
        disabled={busy || !filled}
        onClick={() => void submit()}
      >
        {busy ? '추가하는 중…' : '친구 추가'}
      </button>
    </div>
  );
}
